import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLocationFilter } from '../lib/location-context';
import {
  HeaderSlotSetterProvider,
  type HeaderSlot,
} from '../lib/header-slot';
import { OverflowMenu } from './OverflowMenu';

// Thin top bar + hamburger drawer. The center column hosts a per-route slot
// (filled via useHeaderSlot in screen components) so each screen can mount its
// own controls inline. Mobile collapses extras into an overflow menu; desktop
// gets an optional second strip below the header.
export function Layout() {
  const { operator, session, signOut, can } = useAuth();
  // External partner logins (Evoluon reporters) lack booking_staff_access:
  // they get no calendar, no location switcher and no setup section.
  const staff = can('booking_staff_access');
  const { locationId, setLocationId, locations } = useLocationFilter();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [slot, setSlot] = useState<HeaderSlot | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const setSlotStable = useCallback((s: HeaderSlot | null) => setSlot(s), []);

  const displayEmail = operator?.email ?? session?.user?.email ?? '';

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const goSetup = (tab: string) => {
    setDrawerOpen(false);
    navigate(`/setup?tab=${tab}`);
  };

  const overflowPanel = useMemo(() => slot?.overflowPanel ?? null, [slot]);

  return (
    <div className="app-shell">
      <div className="app-header-stack">
        <header className="app-header">
          <button
            className="app-header-hamburger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menu"
            aria-expanded={drawerOpen}
          >
            <span className="app-header-hamburger-icon" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <div className="app-header-slot">{slot?.row1}</div>
          {staff ? (
          <select
            className="app-header-location-select"
            value={locationId ?? ''}
            onChange={(e) => setLocationId(e.target.value || null)}
            aria-label="Locatie"
          >
            <option value="">Alle locaties</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.display_name ?? l.name}
              </option>
            ))}
          </select>
          ) : null}
          {overflowPanel ? <OverflowMenu panel={overflowPanel} /> : null}
        </header>
        <div className="app-header-row2">{slot?.desktopRow2}</div>
      </div>
      <div className="app-fab-slot">{slot?.fab}</div>

      {drawerOpen ? (
        <div
          className="app-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      ) : null}
      <aside
        className={`app-drawer ${drawerOpen ? 'app-drawer-open' : ''}`}
        aria-hidden={!drawerOpen}
      >
        <div className="app-drawer-head">
          <span className="app-drawer-title">Menu</span>
          <button
            className="app-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>

        {staff ? (
        <div className="app-drawer-section app-drawer-location">
          <div className="app-drawer-section-title">Locatie</div>
          <select
            className="app-drawer-location-select"
            value={locationId ?? ''}
            onChange={(e) => {
              setLocationId(e.target.value || null);
              setDrawerOpen(false);
            }}
            aria-label="Locatie"
          >
            <option value="">Alle locaties</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.display_name ?? l.name}
              </option>
            ))}
          </select>
        </div>
        ) : null}

        <nav className="app-drawer-nav">
          {staff ? (
            <>
              <NavLink to="/calendar" className={navItemClass}>Kalender</NavLink>
              <NavLink to="/today" className={navItemClass}>Vandaag</NavLink>
              <NavLink to="/scan" className={navItemClass}>Scan</NavLink>
            </>
          ) : null}
          {can('booking_view_sales') ? (
            <NavLink to="/sales" className={navItemClass}>Sales</NavLink>
          ) : null}
          {can('booking_view_analytics') ? (
            <NavLink to="/analytics" className={navItemClass}>Analyse</NavLink>
          ) : null}
          {staff ? (
            <>
              <NavLink to="/search" className={navItemClass}>Zoek</NavLink>
              <NavLink to="/vouchers" className={navItemClass}>Vouchers</NavLink>
              <NavLink to="/cadeaubonnen" className={navItemClass}>Cadeaubonnen</NavLink>
            </>
          ) : null}
          {can('booking_team_outings') ? (
            <NavLink to="/teamuitjes" className={navItemClass}>Teamuitjes</NavLink>
          ) : null}
          {can('booking_marketing') ? (
            <NavLink to="/marketing" className={navItemClass}>Marketing</NavLink>
          ) : null}
          {can('booking_view_transactions') ? (
            <NavLink to="/payments" className={navItemClass}>Transacties</NavLink>
          ) : null}
          {can('booking_manage_external_runs') ? (
            <NavLink to="/runs" className={navItemClass}>Runs</NavLink>
          ) : null}
          {can('booking_report_external_runs') ? (
            <NavLink to="/meldingen" className={navItemClass}>Meldingen</NavLink>
          ) : null}
          {can('support_manage_intents') ? (
            <NavLink to="/chatbot" className={navItemClass}>Chatbot</NavLink>
          ) : null}
        </nav>

        {staff ? (
        <div className="app-drawer-section">
          <div className="app-drawer-section-title">Setup</div>
          <button className="app-drawer-link" onClick={() => goSetup('experiences')}>
            Experiences
          </button>
          <button className="app-drawer-link" onClick={() => goSetup('schedule')}>
            Schedule
          </button>
          <button className="app-drawer-link" onClick={() => goSetup('overrides')}>
            Overrides
          </button>
          <button className="app-drawer-link" onClick={() => goSetup('locations')}>
            Locations
          </button>
        </div>
        ) : null}

        <div className="app-drawer-foot">
          <div className="app-drawer-email">{displayEmail}</div>
          <button className="btn-link" onClick={signOut}>Uitloggen</button>
        </div>
      </aside>

      <main className="app-main">
        <HeaderSlotSetterProvider value={setSlotStable}>
          <Outlet />
        </HeaderSlotSetterProvider>
      </main>
    </div>
  );
}

function navItemClass({ isActive }: { isActive: boolean }): string {
  return `app-drawer-link${isActive ? ' app-drawer-link-active' : ''}`;
}
